// S3 side-by-side version diff. Fetches the chosen comparison version and feeds
// both line sets into the foundation DiffView, which highlights added / removed /
// changed lines. Cost is excluded (structure diff only, helpers.linesToDiffRows).

import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { CardSkeleton, ErrorState } from "../../../../../components/states";
import { DiffView } from "../../components/version-switcher";
import { requestIdOf } from "../../../../../lib/request";
import { useBom } from "../../hooks/catalogue-queries";
import { fromBomLine, linesToDiffRows, type EditableLine } from "./helpers";

type Product = components["schemas"]["Product"];

export function BomDiff({
  currentLines,
  currentVersion,
  compareBomId,
  productsById,
  uomLabel,
}: {
  currentLines: readonly EditableLine[];
  currentVersion: number;
  compareBomId: string;
  productsById: ReadonlyMap<string, Product>;
  uomLabel: (uomId: string | null) => string;
}) {
  const { t } = useTranslation();
  const compareQ = useBom(compareBomId);

  if (compareQ.isPending) {
    return <CardSkeleton lines={4} label={t("catalogue.bomEditor.diff.loading")} />;
  }
  if (compareQ.error) {
    const detail = compareQ.error instanceof Error ? compareQ.error.message : undefined;
    return (
      <ErrorState
        title={t("catalogue.bomEditor.errorTitle")}
        {...(detail !== undefined ? { detail } : {})}
        retryLabel={t("catalogue.state.retry")}
        onRetry={() => void compareQ.refetch()}
        {...(requestIdOf(compareQ.error) !== undefined
          ? { requestId: requestIdOf(compareQ.error) }
          : {})}
        requestIdLabel={t("catalogue.state.requestId")}
      />
    );
  }

  const compare = compareQ.data;
  if (!("lines" in compare)) {
    // The explode shape should never come back here (no explode query passed).
    return null;
  }
  const uomLabelStr = (uomId: string) => uomLabel(uomId);
  const atStart = t("catalogue.bomEditor.inspector.atWoStart");

  const before = linesToDiffRows(
    compare.lines.map(fromBomLine),
    productsById,
    uomLabelStr,
    atStart,
  );
  const after = linesToDiffRows(currentLines, productsById, uomLabelStr, atStart);

  return (
    <div className="bom-diff" data-testid="bom-diff">
      <p className="bom-diff-caption">
        {t("catalogue.bomEditor.version", { version: compare.version })} →{" "}
        {t("catalogue.bomEditor.version", { version: currentVersion })}
      </p>
      <DiffView before={before} after={after} />
    </div>
  );
}
